// Small sound-effect player. Pre-creates and caches HTMLAudioElement
// instances so subsequent plays are instant (no fetch on every tap).
//
// Senior + accessibility considerations:
//   - Calls .play() inside a user-gesture handler, which iOS Safari
//     requires. Catches the AbortError that fires when a play is
//     interrupted by a rapid second tap (harmless, just suppress).
//   - Falls back to silent when window.Audio is unavailable (SSR /
//     tests / very old browsers).
//   - Volume capped at 0.6 so the ding never startles.
//
// Future: gate on a user-controlled `soundsEnabled` accessibility
// preference (server-persisted). For V1 sounds are always on for
// the in-app check-off ding (one short non-intrusive sound on a
// deliberate tap action — the safest possible default).

import { useCallback } from 'react';

const PATHS = {
  'med-check-ding': '/audio/med-check-ding.mp3',
  'reminder-chime': '/audio/reminder-chime.mp3',
} as const;

type SoundKey = keyof typeof PATHS;

const cache: Partial<Record<SoundKey, HTMLAudioElement>> = {};

function getAudio(key: SoundKey): HTMLAudioElement | null {
  if (typeof window === 'undefined' || typeof Audio === 'undefined') return null;
  const existing = cache[key];
  if (existing) return existing;
  const el = new Audio(PATHS[key]);
  el.preload = 'auto';
  el.volume = 0.6;
  cache[key] = el;
  return el;
}

export function useSoundEffect(): (key: SoundKey) => void {
  return useCallback((key: SoundKey) => {
    const el = getAudio(key);
    if (!el) return;
    // Rewind so rapid repeated plays still ding from the start.
    try {
      el.currentTime = 0;
    } catch {
      // Some browsers throw if the element is mid-load; harmless.
    }
    void el.play().catch(() => {
      // AbortError on rapid retoggles, NotAllowedError on first paint
      // before any user gesture — both safe to swallow. The next tap
      // will succeed.
    });
  }, []);
}
